'use client';

import { Fragment, useEffect, useRef } from 'react';
import clsx from 'clsx';
import AppLink from '@/components/i18n/AppLink';
import { Li, Ol } from '@/components/ui';
import { widgetSurfaceClass } from '@/components/widget/_lib/widgetStandard';

export type BreadcrumbItem = {
  label: string;
  href?: string;
};

export type BreadcrumbsProps = Omit<React.ComponentPropsWithoutRef<'nav'>, 'children'> & {
  items: readonly BreadcrumbItem[];
  /** Horizontal scroll on narrow viewports */
  scrollable?: boolean;
};

function BreadcrumbChevron() {
  return (
    <svg
      className="widget-breadcrumbs__sep"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <path
        d="M6.22 4.22a.75.75 0 0 1 1.06 0L10.94 8l-3.66 3.78a.75.75 0 1 1-1.06-1.06L8.88 8 6.22 5.28a.75.75 0 0 1 0-1.06z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function Breadcrumbs({
  items,
  className,
  scrollable = true,
  ...navProps
}: BreadcrumbsProps) {
  const listRef = useRef<HTMLOListElement>(null);
  const collapseMiddleOnMobile = scrollable && items.length > 3;
  const parentCrumb =
    collapseMiddleOnMobile && items.length > 1 ? items[items.length - 2] : undefined;

  useEffect(() => {
    const list = listRef.current;
    if (!list || !scrollable) return;

    const scrollToEnd = () => {
      list.scrollLeft = list.scrollWidth;
    };

    scrollToEnd();
    const observer = new ResizeObserver(scrollToEnd);
    observer.observe(list);
    return () => observer.disconnect();
  }, [items, scrollable]);

  if (!items.length) return null;

  return (
    <nav
      className={widgetSurfaceClass(
        'breadcrumbs',
        clsx(
          'widget-breadcrumbs',
          scrollable && 'widget-breadcrumbs--scroll',
          collapseMiddleOnMobile && 'widget-breadcrumbs--collapse-mobile',
          className
        )
      )}
      data-widget="Breadcrumbs"
      aria-label="Breadcrumb"
      {...navProps}
    >
      <Ol ref={listRef} className="widget-breadcrumbs__list ol m-0 list-none p-0">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isFirst = index === 0;
          const isMiddle = !isFirst && !isLast;
          const isLink = Boolean(item.href) && !isLast;

          return (
            <Fragment key={`${item.label}-${index}`}>
              {collapseMiddleOnMobile && isLast ? (
                <Li className="li widget-breadcrumbs__item widget-breadcrumbs__item--ellipsis sm:hidden">
                  <BreadcrumbChevron />
                  {parentCrumb?.href ? (
                    <AppLink
                      href={parentCrumb.href}
                      className="widget-breadcrumbs__ellipsis anchor"
                      aria-label={parentCrumb.label}
                    >
                      …
                    </AppLink>
                  ) : (
                    <span className="widget-breadcrumbs__ellipsis" aria-hidden>
                      …
                    </span>
                  )}
                </Li>
              ) : null}
              <Li
                className={clsx(
                  'li widget-breadcrumbs__item',
                  collapseMiddleOnMobile && isMiddle && 'max-sm:hidden'
                )}
              >
                {index > 0 ? <BreadcrumbChevron /> : null}
                {isLink && item.href ? (
                  <AppLink href={item.href} className="widget-breadcrumbs__link anchor">
                    {item.label}
                  </AppLink>
                ) : (
                  <span
                    className={clsx(
                      'widget-breadcrumbs__current',
                      isLast && 'widget-breadcrumbs__current--page'
                    )}
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {item.label}
                  </span>
                )}
              </Li>
            </Fragment>
          );
        })}
      </Ol>
    </nav>
  );
}

Breadcrumbs.displayName = 'Breadcrumbs';
